@if (!drawerMode) {
  <app-page-breadcrumb [pageTitle]="pageTitle"></app-page-breadcrumb>
}

@if (drawerMode) {
  <ng-container *ngTemplateOutlet="formContent"></ng-container>
} @else {
  <div class="grid grid-cols-1 gap-6">
    <app-component-card
      [title]="pageTitle"
      desc="Complete los datos del negocio"
    >
      <ng-container *ngTemplateOutlet="formContent"></ng-container>
    </app-component-card>
  </div>
}

<ng-template #formContent>
  <!-- Oportunidad cerrado - Información de cierre -->
  @if (isClosed && currentDeal) {
    <div
      class="mb-6 p-4 bg-warning-50 border border-warning-200 rounded-lg dark:bg-warning-500/10 dark:border-warning-500/20"
    >
      <div class="flex items-start gap-3">
        <svg
          class="w-5 h-5 text-warning-500 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          ></path>
        </svg>
        <div class="flex-1">
          <h4 class="font-semibold text-warning-800 dark:text-warning-300 mb-1">
            Este deal está cerrado
          </h4>
          <p class="text-sm text-warning-700 dark:text-warning-400 mb-2">
            Cerrado el {{ formatDate(currentDeal.datetimeClosed) }}
          </p>
          @if (currentDeal.descriptionForClose) {
            <p class="text-sm text-warning-700 dark:text-warning-400">
              <strong>Razón:</strong> {{ currentDeal.descriptionForClose }}
            </p>
          }
          <p class="text-sm text-warning-700 dark:text-warning-400 mt-2">
            Para editar este negocio, primero debe reabrirlo desde la vista de
            detalle.
          </p>
        </div>
      </div>
    </div>
  }

  <!-- Loading -->
  @if (loading) {
    <div class="flex justify-center items-center py-12">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"
      ></div>
      <span class="ml-3 text-gray-500 dark:text-gray-400"
        >Cargando datos...</span
      >
    </div>
  }

  <!-- Error -->
  @if (error) {
    <div
      class="mb-4 p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20"
    >
      <div class="flex items-center gap-3">
        <svg
          class="w-5 h-5 text-error-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          ></path>
        </svg>
        <p class="text-error-600 dark:text-error-400">{{ error }}</p>
      </div>
    </div>
  }

  <!-- Formulario -->
  @if (!loading) {
    <form
      [formGroup]="form"
      (ngSubmit)="guardar()"
      class="space-y-5"
    >
      <!-- Responsable del negocio. La columna es int_id_user_propiety (owner) pero en la
           interfaz el término del negocio es «Responsable», en todos los módulos. -->
      <div>
        <label
          class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >Responsable <span class="text-error-500">*</span></label
        >
        <select
          formControlName="userPropietyId"
          class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
          [class.border-error-500]="hasError('userPropietyId')"
          [class.border-gray-300]="!hasError('userPropietyId')"
        >
          @for (user of users; track user.id) {
            <option [ngValue]="user.id">
              {{ user.fullname }} {{ user.lastname }}
            </option>
          }
        </select>
        @if (hasError('userPropietyId')) {
          <p class="mt-1.5 text-sm text-error-500">
            {{ getError('userPropietyId') }}
          </p>
        }
      </div>
      <!-- Nombre del Oportunidad -->
      <div>
        <label
          class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Nombre del Negocio <span class="text-error-500">*</span>
        </label>
        <input
          type="text"
          formControlName="name"
          placeholder="Nombre descriptivo del negocio"
          class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
          [class.border-error-500]="hasError('name')"
          [class.border-gray-300]="!hasError('name')"
        />
        @if (hasError('name')) {
          <p class="mt-1.5 text-sm text-error-500">{{ getError('name') }}</p>
        }
      </div>

      <!-- Descripción -->
      <div>
        <label
          class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Descripción
        </label>
        <textarea
          formControlName="description"
          rows="3"
          placeholder="Descripción detallada del negocio"
          class="dark:bg-dark-900 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
          [class.border-error-500]="hasError('description')"
          [class.border-gray-300]="!hasError('description')"
          maxlength="1001"
        ></textarea>
        @if (hasError('description')) {
          <p class="mt-1.5 text-sm text-error-500">
            {{ getError('description') }}
          </p>
        }
      </div>

      <!-- Moneda -->
      <div>
        <label
          class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Moneda <span class="text-error-500">*</span>
        </label>
        <select
          formControlName="prefix"
          class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
          [class.border-error-500]="hasError('prefix')"
          [class.border-gray-300]="!hasError('prefix')"
        >
          <option value="PEN">PEN - Sol Peruano (S/)</option>
          <option value="USD">USD - Dólar Americano ($)</option>
          <option value="EUR">EUR - Euro (€)</option>
          <option value="MXN">MXN - Peso Mexicano ($)</option>
        </select>
        @if (hasError('prefix')) {
          <p class="mt-1.5 text-sm text-error-500">{{ getError('prefix') }}</p>
        }
      </div>

      <!-- Valor total del oportunidad-->
      <div>
        <label
          class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Valor ({{ selectedCurrency }})
        </label>
        <input
          placeholder="Valor total del negocio"
          type="number"
          formControlName="amount"
          min="0.1"
          class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
          [class.border-error-500]="hasError('amount')"
          [class.border-gray-300]="!hasError('amount')"
        />
      </div>

      <!-- Etapa del Oportunidad -->
      <div>
        <label
          class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Etapa del Negocio <span class="text-error-500">*</span>
        </label>
        <select
          formControlName="idDealCycle"
          class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
          [class.border-error-500]="hasError('idDealCycle')"
          [class.border-gray-300]="!hasError('idDealCycle')"
        >
          <option [ngValue]="null">Seleccione una etapa</option>
          @for (cycle of dealCycles; track cycle.id) {
            <option [ngValue]="cycle.id">{{ cycle.name }}</option>
          }
        </select>
        @if (hasError('idDealCycle')) {
          <p class="mt-1.5 text-sm text-error-500">
            {{ getError('idDealCycle') }}
          </p>
        }
        @if (loadingCatalogs) {
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Cargando etapas...
          </p>
        }
      </div>

      <!-- Tipo de Negocio, Prioridad y Fecha Estimada de Cierre -->
      <div class="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div>
          <label
            class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Tipo de Negocio
          </label>
          <select
            formControlName="idDealBusinessType"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option [ngValue]="null">Sin tipo</option>
            @for (bt of activesDealBusinessTypes(); track bt.id) {
              <option [ngValue]="bt.id">{{ bt.name }}</option>
            }
          </select>
          @if (loadingCatalogs) {
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Cargando...
            </p>
          }
        </div>

        <div>
          <label
            class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Prioridad
          </label>
          <select
            formControlName="idDealPriority"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option [ngValue]="null">Sin prioridad</option>
            @for (p of dealPriorities; track p.id) {
              <option [ngValue]="p.id">{{ p.name }}</option>
            }
          </select>
          @if (loadingCatalogs) {
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Cargando...
            </p>
          }
        </div>

        <div>
          <label
            class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Fecha Estimada de Cierre
          </label>
          <input
            type="datetime-local"
            [min]="minDatetimeLocal"
            formControlName="datetimeEstimatedClose"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
            [class.border-error-500]="hasError('datetimeEstimatedClose')"
            [class.border-gray-300]="!hasError('datetimeEstimatedClose')"
          />
          @if (hasError('datetimeEstimatedClose')) {
            <p class="mt-1.5 text-sm text-error-500">
              {{ getError('datetimeEstimatedClose') }}
            </p>
          }
        </div>
      </div>

      <!-- Contactos Asociados -->
      <div
        class="pt-4 border-t border-gray-200 dark:border-gray-700"
        [class.border-error-300]="submitAttempted && contactsInvalid"
        [class.dark:border-error-700]="submitAttempted && contactsInvalid"
      >
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2">
            <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Contactos Asociados <span class="text-error-500">*</span>
            </h3>
            @if (!isClosed) {
              <button
                type="button"
                (click)="abrirNuevoContacto()"
                title="Crear nuevo contacto"
                class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 dark:text-brand-400 dark:hover:text-brand-300 dark:hover:bg-brand-500/10 rounded-md transition-colors"
              >
                <svg
                  class="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4v16m8-8H4"
                  ></path>
                </svg>
                Nuevo
              </button>
            }
          </div>
          @if (submitAttempted && contactsInvalid) {
            <span
              class="text-xs text-error-500 font-medium flex items-center gap-1"
            >
              <svg
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              Agrega al menos un contacto
            </span>
          }
        </div>

        @if (!isClosed) {
          <div class="mb-4">
            <!-- Search input always visible -->
            <div class="relative">
              <input
                type="text"
                [value]="contactSearchTerm"
                (input)="onContactSearchInput($any($event.target).value)"
                (keydown.enter)="onContactSearchEnter($event)"
                (blur)="closeContactDropdown()"
                placeholder="Buscar contacto... (Enter para ver todos)"
                class="dark:bg-dark-900 h-10 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
              />

              @if (searchingContacts) {
                <div class="absolute right-3 top-3">
                  <div
                    class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"
                  ></div>
                </div>
              }

              @if (showContactDropdown && contactSearchResults.length > 0) {
                <div
                  class="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 max-h-48 overflow-y-auto"
                  (scroll)="onDropdownScroll($event)"
                >
                  @for (c of contactSearchResults; track c.id) {
                    <button
                      type="button"
                      (mousedown)="selectContactFromDropdown(c)"
                      class="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <span
                        class="block text-sm font-medium text-gray-900 dark:text-white"
                      >
                        {{ c.fullname }} {{ c.lastname || '' }}
                      </span>
                      <span
                        class="block text-xs text-gray-500 dark:text-gray-400"
                        >{{ c.mail }}</span
                      >
                    </button>
                  }
                  @if (loadingMoreContacts) {
                    <div class="flex justify-center items-center py-2">
                      <div
                        class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"
                      ></div>
                      <span
                        class="ml-2 text-xs text-gray-400 dark:text-gray-500"
                        >Cargando más...</span
                      >
                    </div>
                  }
                </div>
              }
            </div>

            <!-- Extra fields: only visible after selecting a contact -->
            @if (selectedContactForAdd) {
              <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label
                    class="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300"
                  >
                    Rol en el Deal
                  </label>
                  <select
                    [(ngModel)]="newContactRole"
                    [ngModelOptions]="{ standalone: true }"
                    class="dark:bg-dark-900 h-10 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                  >
                    <option [ngValue]="undefined">Sin rol</option>
                    @for (role of contactRoles; track role.id) {
                      <option [ngValue]="role.id">{{ role.name }}</option>
                    }
                  </select>
                </div>
                <div class="flex items-end">
                  <button
                    type="button"
                    (click)="agregarContacto()"
                    class="h-10 w-full px-4 text-sm font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 4v16m8-8H4"
                      ></path>
                    </svg>
                    Agregar
                  </button>
                </div>
              </div>
              <div class="mt-2">
                <textarea
                  [(ngModel)]="newContactNotes"
                  [ngModelOptions]="{ standalone: true }"
                  rows="2"
                  maxlength="2000"
                  placeholder="Notas sobre la relación con este contacto... (opcional)"
                  class="dark:bg-dark-900 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
                ></textarea>
              </div>
            }
          </div>
        }

        <!-- Loading associations -->
        @if (loadingContactAssociations) {
          <div
            class="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-2"
          >
            <div
              class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"
            ></div>
            <span class="text-sm">Cargando contactos asociados...</span>
          </div>
        }

        <!-- Contact list -->
        @if (dealContacts.length > 0) {
          <div class="space-y-2">
            @for (dc of dealContacts; track dc.contact.id; let i = $index) {
              <div
                class="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
              >
                <!-- Avatar -->
                <div
                  class="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                  [ngClass]="getContactAvatarColor(dc.contact)"
                >
                  {{ getContactInitials(dc.contact) }}
                </div>

                <!-- Info -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span
                      class="text-sm font-medium text-gray-900 dark:text-white"
                    >
                      {{ dc.contact.fullname }} {{ dc.contact.lastname || '' }}
                    </span>

                    <!-- Primary badge -->
                    @if (dc.isPrimary) {
                      <span
                        class="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-400"
                      >
                        Principal
                      </span>
                    }
                    <!-- Role badge -->
                    @if (dc.idDealContactRole) {
                      <span
                        class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-400"
                      >
                        {{ getRoleName(dc.idDealContactRole) }}
                      </span>
                    }
                  </div>

                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {{ dc.contact.mail }}
                  </p>
                  @if (dc.contact.phone) {
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      {{ dc.contact.phone }}
                    </p>
                  }
                  @if (dc.notes) {
                    <p
                      class="text-xs text-gray-400 dark:text-gray-500 italic mt-1 truncate max-w-sm"
                    >
                      {{ dc.notes }}
                    </p>
                  }
                </div>

                <!-- Actions -->
                @if (!isClosed) {
                  <div class="shrink-0 flex items-center gap-1">
                    <!-- Toggle primary button -->
                    <button
                      type="button"
                      (click)="togglePrimaryContact(i)"
                      [title]="
                        dc.isPrimary
                          ? 'Quitar como principal'
                          : 'Marcar como principal'
                      "
                      [class]="
                        dc.isPrimary
                          ? 'p-1.5 rounded-lg text-success-600 bg-success-50 dark:bg-success-500/10 hover:opacity-70 transition-opacity'
                          : 'p-1.5 rounded-lg text-gray-400 hover:text-success-600 hover:bg-success-50 dark:hover:bg-success-500/10 transition-colors'
                      "
                    >
                      <svg
                        class="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                        />
                      </svg>
                    </button>
                    <!-- Remove button -->
                    <button
                      type="button"
                      (click)="quitarContacto(i)"
                      class="p-1.5 rounded-lg text-gray-400 hover:text-error-500 hover:bg-error-50 dark:hover:bg-error-500/10 transition-colors"
                      title="Quitar contacto"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"
                        ></path>
                      </svg>
                    </button>
                  </div>
                }
              </div>
            }
          </div>
        } @else if (!loadingContactAssociations) {
          <p
            class="text-sm py-2"
            [class.text-error-500]="submitAttempted"
            [class.text-gray-500]="!submitAttempted"
            [class.dark:text-gray-400]="!submitAttempted"
          >
            @if (submitAttempted) {
              Debes agregar al menos un contacto para continuar.
            } @else {
              No hay contactos asociados a este negocio.
            }
          </p>
        }
      </div>

      <!-- Empresa -->
      <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
        <div class="flex items-center gap-2 mb-3">
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Empresa Asociada
          </h3>
          @if (!isClosed) {
            <button
              type="button"
              (click)="abrirNuevaEmpresa()"
              title="Crear nueva empresa"
              class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 dark:text-brand-400 dark:hover:text-brand-300 dark:hover:bg-brand-500/10 rounded-md transition-colors"
            >
              <svg
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                ></path>
              </svg>
              Nuevo
            </button>
          }
          @if (companyAutoFilled) {
            <span
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700 dark:bg-success-500/20 dark:text-success-400 animate-pulse"
            >
              <svg
                class="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
              Auto-completado
            </span>
          }
        </div>
        <div class="relative">
          <input
            type="text"
            [value]="companySearchTerm"
            (input)="onCompanySearchInput($any($event.target).value)"
            (keydown.enter)="onCompanySearchEnter($event)"
            (blur)="closeCompanyDropdown()"
            [disabled]="isClosed"
            placeholder="Buscar empresa... (Enter para ver todas)"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-500 dark:text-white/90 dark:placeholder:text-white/30"
            [class.border-success-400]="companyAutoFilled"
            [class.bg-success-50]="companyAutoFilled"
            [class.dark:bg-success-500\/10]="companyAutoFilled"
            [class.ring-success-500\/20]="companyAutoFilled"
            [class.focus:border-success-400]="companyAutoFilled"
            [class.border-gray-300]="!companyAutoFilled"
            [class.focus:border-brand-300]="!companyAutoFilled"
            [class.focus:ring-brand-500\/10]="!companyAutoFilled"
            [class.dark:border-gray-700]="!companyAutoFilled"
            [class.dark:bg-gray-900]="!companyAutoFilled"
          />

          @if (companySearchTerm && !isClosed) {
            <button
              type="button"
              (click)="clearCompany()"
              class="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          }

          @if (searchingCompanies) {
            <div class="absolute right-3 top-3">
              <div
                class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"
              ></div>
            </div>
          }

          @if (showCompanyDropdown && companySearchResults.length > 0) {
            <div
              class="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 max-h-48 overflow-y-auto"
              (scroll)="onCompanyDropdownScroll($event)"
            >
              @for (company of companySearchResults; track company.id) {
                <button
                  type="button"
                  (mousedown)="selectCompanyFromDropdown(company)"
                  class="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <span
                    class="block text-sm font-medium text-gray-900 dark:text-white"
                    >{{ company.commercialName }}</span
                  >
                  @if (company.businessName) {
                    <span
                      class="block text-xs text-gray-500 dark:text-gray-400"
                      >{{ company.businessName }}</span
                    >
                  }
                </button>
              }
              @if (loadingMoreCompanies) {
                <div class="flex justify-center items-center py-2">
                  <div
                    class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"
                  ></div>
                  <span class="ml-2 text-xs text-gray-400 dark:text-gray-500"
                    >Cargando más...</span
                  >
                </div>
              }
            </div>
          }
        </div>
      </div>

      <!-- Botones de acción -->
      <div
        class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700"
      >
        <app-button
          variant="outline"
          type="button"
          (btnClick)="cancelar()"
        >
          Cancelar
        </app-button>
        @if (!isClosed) {
          <app-button
            variant="primary"
            type="button"
            (btnClick)="guardar()"
            [disabled]="
              saving || form.invalid || (submitAttempted && contactsInvalid)
            "
          >
            {{
              saving
                ? 'Guardando...'
                : isEditing
                  ? 'Actualizar'
                  : 'Crear Negocio'
            }}
          </app-button>
        }
      </div>
    </form>
  }

  <!-- Modal: elegir empresa del contacto -->
  @if (showCompanyChoiceModal) {
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md mx-4"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700"
        >
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">
            Empresas asociadas al contacto
          </h3>
          <button
            type="button"
            (click)="skipCompanyInModal()"
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
        <div class="px-6 py-4">
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            El contacto está asociado a múltiples empresas. Selecciona una para
            asociarla al deal, o cierra para no asociar ninguna.
          </p>
          <div class="space-y-2">
            @for (assoc of contactCompaniesForModal; track assoc.id) {
              <button
                type="button"
                (click)="selectCompanyInModal(assoc)"
                class="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-brand-50 hover:border-brand-300 dark:hover:bg-brand-500/10 dark:hover:border-brand-500/30 transition-colors text-left"
              >
                <div
                  class="shrink-0 w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center"
                >
                  <svg
                    class="w-4 h-4 text-gray-500 dark:text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    ></path>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <p
                    class="text-sm font-medium text-gray-900 dark:text-white truncate"
                  >
                    {{
                      assoc.company?.commercialName ||
                        'Empresa #' + assoc.idCompany
                    }}
                  </p>
                  @if (assoc.isPrimary) {
                    <span
                      class="text-xs text-brand-600 dark:text-brand-400 font-medium"
                      >Principal</span
                    >
                  }
                  @if (assoc.position) {
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      {{ assoc.position }}
                    </p>
                  }
                </div>
                <svg
                  class="w-4 h-4 text-gray-400 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  ></path>
                </svg>
              </button>
            }
          </div>
        </div>
        <div
          class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end"
        >
          <button
            type="button"
            (click)="skipCompanyInModal()"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            No asociar empresa
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Inc 11 — Modal: negocio duplicado -->
  @if (showDuplicateModal) {
    <div
      class="fixed inset-0 z-modal-top flex items-center justify-center bg-black/50 px-4"
      (click)="cerrarDuplicadoModal()"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-md"
        (click)="$event.stopPropagation()"
      >
        <div class="px-6 py-5 text-center">
          <div
            class="w-14 h-14 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mx-auto mb-4"
          >
            <svg
              class="w-7 h-7 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              ></path>
            </svg>
          </div>
          <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">
            Negocio duplicado
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Ya existe un negocio
            <strong class="text-gray-700 dark:text-gray-200"
              >'{{ duplicateExistingName }}'</strong
            >
            con el mismo nombre, empresa y contacto. ¿Deseas abrir el existente
            o crear uno nuevo de todos modos?
          </p>
        </div>
        <div
          class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-center gap-3"
        >
          <button
            type="button"
            (click)="cerrarDuplicadoModal()"
            class="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Cancelar
          </button>
          @if (duplicateExistingId) {
            <button
              type="button"
              (click)="abrirNegocioExistente()"
              class="px-4 py-2.5 text-sm font-medium text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-700 rounded-xl hover:bg-brand-100 dark:hover:bg-brand-900/40 transition-colors"
            >
              Abrir el existente
            </button>
          }
          <button
            type="button"
            (click)="confirmarCrearDuplicado()"
            class="px-4 py-2.5 text-sm font-medium text-white bg-brand-500 rounded-xl hover:bg-brand-600 transition-colors"
          >
            Crear de todos modos
          </button>
        </div>
      </div>
    </div>
  }
</ng-template>
